import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import {
  apiClient,
  type EthnicityDto,
  type EthnicityTermDto,
  type LiveDemographicsBody,
  type LiveSexCode,
  type PatientPhysicalChartDto,
  type components,
} from '@/api/client';
import { patientQueries } from '@/features/patient-record/queries';
import type {
  AddressViewModel,
  ContactPointViewModel,
  DemographicsViewModel,
  EthnicityViewModel,
  PatientViewModel,
  PersonIdentifierViewModel,
  PhysicalChartViewModel,
} from '@/models/patient';

type DemographicsDto = components['schemas']['Demographics'];
type AddressDto = components['schemas']['Address'];
type ContactPointDto = components['schemas']['ContactPoint'];
type PersonIdentifierDto = components['schemas']['PersonIdentifier'];

/** Existing record may already carry PTV2-1064 fields the checked-in schema
 *  doesn't surface; widen reads so the merge can preserve unknown fields. */
type ExistingDemographics = DemographicsDto & Partial<LiveDemographicsBody>;

/**
 * Resource-level mutations for the patient detail record (demographics +
 * identifiers). Bundled here because every operation invalidates the
 * same `patientQueries.detail(guid)` key and the optimistic updates all
 * splice into one cached `PatientViewModel`.
 *
 * Operations:
 *   - updateDemographics(next)  — replace the demographics blob
 *                                 (firstName, lastName, sex, dob, gender,
 *                                 lifeStatus, contactPoints, addresses)
 *   - addIdentifier(input)      — append a new identifier
 *   - replaceIdentifiers(list)  — replace the entire identifier list
 *   - removeIdentifier(id)      — delete a single identifier
 *
 * Each follows the locked optimistic-prepend + rollback pattern.
 */

interface PatientMutationContext {
  readonly previous: PatientViewModel | undefined;
}

interface IdentifierAddInput {
  readonly identifierType: string;
  readonly label: string | null;
  readonly value: string;
}

type UpdateDemographicsResult = UseMutationResult<
  DemographicsViewModel,
  Error,
  DemographicsViewModel,
  PatientMutationContext
>;
type AddIdentifierResult = UseMutationResult<
  PersonIdentifierViewModel,
  Error,
  IdentifierAddInput,
  PatientMutationContext
>;
type ReplaceIdentifiersResult = UseMutationResult<
  readonly PersonIdentifierViewModel[],
  Error,
  readonly PersonIdentifierViewModel[],
  PatientMutationContext
>;
type RemoveIdentifierResult = UseMutationResult<
  undefined,
  Error,
  string,
  PatientMutationContext
>;

export interface PatientMutations {
  readonly updateDemographics: UpdateDemographicsResult;
  readonly addIdentifier: AddIdentifierResult;
  readonly replaceIdentifiers: ReplaceIdentifiersResult;
  readonly removeIdentifier: RemoveIdentifierResult;
}

export function usePatientMutations(guid: string): PatientMutations {
  const queryClient = useQueryClient();
  const { queryKey } = patientQueries.detail(guid);

  const snapshot = (): PatientMutationContext => ({
    previous: queryClient.getQueryData<PatientViewModel>(queryKey),
  });

  const rollback = (context: PatientMutationContext | undefined): void => {
    if (context === undefined) return;
    queryClient.setQueryData(queryKey, context.previous);
  };

  const settle = (): void => {
    queryClient.invalidateQueries({ queryKey }).catch(() => undefined);
    // Also refresh the browse/landing lists so an edited patient shows its new
    // values there — not just on the detail record. Without this, the list's
    // staleTime serves the pre-edit cache until a hard reload.
    queryClient.invalidateQueries({ queryKey: ['recent-patients'] }).catch(() => undefined);
    // ...and the shell QuickSearch index, so a renamed patient is findable.
    queryClient.invalidateQueries({ queryKey: ['persons', 'quick-search-index'] }).catch(() => undefined);
  };

  const updateDemographics = useMutation<
    DemographicsViewModel,
    Error,
    DemographicsViewModel,
    PatientMutationContext
  >({
    meta: { successMessage: 'success.toast.title.demographics_updated' },
    mutationFn: async (next) => {
      // The live `PUT /persons/{guid}/demographics` replaces the entire
      // demographics blob, so we have to send a complete payload. Pull
      // the latest Person fresh and overlay the form's editable fields on
      // top of it — that way ids (names[0].id, contact[].id, address[].id)
      // and unsurfaced fields (prefix/suffix/pronoun/period,
      // addressLine2/district/addressType, lifeStatus.causeOfDeath, etc.)
      // round-trip losslessly.
      const existing = await apiClient.getPerson(guid);
      await apiClient.updateDemographics(
        guid,
        mergeDemographics(existing.demographics as ExistingDemographics | undefined, next),
      );
      return next;
    },
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey });
      const ctx = snapshot();
      if (ctx.previous !== undefined) {
        queryClient.setQueryData<PatientViewModel>(queryKey, {
          ...ctx.previous,
          demographics: next,
        });
      }
      return ctx;
    },
    onError: (_e, _v, ctx) => {
      rollback(ctx);
    },
    onSettled: settle,
  });

  const addIdentifier = useMutation<
    PersonIdentifierViewModel,
    Error,
    IdentifierAddInput,
    PatientMutationContext
  >({
    meta: { successMessage: 'success.toast.title.identifiers_updated' },
    mutationFn: async (input) => {
      // BE rejects `label: null` on `PersonIdentifierDTO` (`@NotNull`); fall
      // back to `userCreatedId` so identifiers added without an explicit
      // label still validate.
      const body: PersonIdentifierDto = {
        userCreatedId: input.identifierType,
        label: input.label ?? input.identifierType,
        value: input.value,
      };
      const dto = await apiClient.addIdentifier(guid, body);
      return identifierFromDto(dto);
    },
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey });
      const ctx = snapshot();
      if (ctx.previous !== undefined) {
        const optimistic: PersonIdentifierViewModel = {
          id: `optimistic-${Date.now().toString(36)}`,
          identifierType: input.identifierType,
          label: input.label,
          value: input.value,
        };
        queryClient.setQueryData<PatientViewModel>(queryKey, {
          ...ctx.previous,
          identifiers: [...ctx.previous.identifiers, optimistic],
        });
      }
      return ctx;
    },
    onError: (_e, _v, ctx) => {
      rollback(ctx);
    },
    onSettled: settle,
  });

  const replaceIdentifiers = useMutation<
    readonly PersonIdentifierViewModel[],
    Error,
    readonly PersonIdentifierViewModel[],
    PatientMutationContext
  >({
    meta: { successMessage: 'success.toast.title.identifiers_updated' },
    mutationFn: async (next) => {
      await apiClient.replaceIdentifiers(guid, next.map(identifierToDto));
      return next;
    },
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey });
      const ctx = snapshot();
      if (ctx.previous !== undefined) {
        queryClient.setQueryData<PatientViewModel>(queryKey, {
          ...ctx.previous,
          identifiers: next,
        });
      }
      return ctx;
    },
    onError: (_e, _v, ctx) => {
      rollback(ctx);
    },
    onSettled: settle,
  });

  const removeIdentifier = useMutation<
    undefined,
    Error,
    string,
    PatientMutationContext
  >({
    meta: { successMessage: 'success.toast.title.identifiers_updated' },
    mutationFn: (id) => apiClient.deleteIdentifier(guid, id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });
      const ctx = snapshot();
      if (ctx.previous !== undefined) {
        queryClient.setQueryData<PatientViewModel>(queryKey, {
          ...ctx.previous,
          identifiers: ctx.previous.identifiers.filter((ident) => ident.id !== id),
        });
      }
      return ctx;
    },
    onError: (_e, _v, ctx) => {
      rollback(ctx);
    },
    onSettled: settle,
  });

  return { updateDemographics, addIdentifier, replaceIdentifiers, removeIdentifier };
}

// ─── DTO ↔ ViewModel helpers ────────────────────────────────────────

/**
 * Maps the FE's letter codes to the live BE's `SexOptions` enum
 * (`[m, f, other, unknown, unassigned, intersex]`). The OpenAPI snapshot
 * still types `Demographics.sex` as `'male' | 'female' | 'other' | 'unknown'`
 * — the override at the API client (`LiveDemographicsBody`) widens the
 * outgoing type so the wire shape is correct.
 */
const SEX_TO_DTO: Readonly<Record<NonNullable<DemographicsViewModel['sex']>, LiveSexCode>> = {
  M: 'm',
  F: 'f',
  O: 'other',
  U: 'unknown',
  INTERSEX: 'intersex',
  UNASSIGNED: 'unassigned',
};

/**
 * Normalizes a sex value carried over from the cached GET (which can be
 * either the OpenAPI long form `'male' | 'female'` or the live single-letter
 * `'m' | 'f'`) to the `SexOptions` codes the BE accepts on PUT/POST.
 */
function normalizeSexForLiveWrite(value: unknown): LiveSexCode | undefined {
  if (typeof value !== 'string') return undefined;
  const lower = value.trim().toLowerCase();
  if (lower.length === 0) return undefined;
  if (lower === 'male') return 'm';
  if (lower === 'female') return 'f';
  if (lower === 'm' || lower === 'f') return lower;
  if (lower === 'other' || lower === 'unknown' || lower === 'unassigned' || lower === 'intersex') {
    return lower;
  }
  return undefined;
}

const LIFE_STATUS_TO_DTO: ReadonlySet<
  NonNullable<NonNullable<DemographicsDto['lifeStatus']>['lifeStatusType']>
> = new Set(['ALIVE', 'ALIVE_AND_WELL', 'DECEASED', 'UNBORN', 'STILL_BORN', 'MISCARRIAGE', 'ABORTED', 'UNKNOWN']);

export function partialDateToDto(value: string | null): DemographicsDto['dateOfBirth'] | undefined {
  if (value === null) return undefined;
  const match = /^(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?$/.exec(value);
  if (match === null) return undefined;
  const year = Number.parseInt(match[1] ?? '', 10);
  if (!Number.isFinite(year)) return undefined;
  const out: NonNullable<DemographicsDto['dateOfBirth']> = { year };
  const month = match[2] !== undefined ? Number.parseInt(match[2], 10) : NaN;
  const day = match[3] !== undefined ? Number.parseInt(match[3], 10) : NaN;
  if (Number.isFinite(month)) out.month = month;
  if (Number.isFinite(day)) out.day = day;
  return out;
}

/**
 * Overlay the editable fields from the form's view model onto the
 * existing demographics DTO. Preserves: top-level `id` / `indexNumber` /
 * `source`, every `names[i]` field except firstName/lastName/lastNameAtBirth, every
 * contact field except (type, value, preferred), every address field
 * except (line, city, province, postalCode, country), and every
 * lifeStatus field except `lifeStatusType`. Matches contacts by
 * (type, value) and addresses by (line, city, postalCode) so the BE keeps
 * record ids stable across edits.
 */
export function mergeDemographics(
  existing: ExistingDemographics | undefined,
  vm: DemographicsViewModel,
): LiveDemographicsBody {
  // Cache holds the OpenAPI long-form (`'male' | 'female' | …`); the wire
  // shape needs `LiveSexCode` (`'m' | 'f' | …`). Spread under the wider
  // type and explicitly normalize the carryover below.
  const base: LiveDemographicsBody = {
    ...(existing as Omit<ExistingDemographics, 'sex'> | undefined),
  };
  stripBlankGuid(base);
  base.names = mergeNames(existing?.names ?? [], vm);
  if (vm.dateOfBirth === null) {
    // Explicit clear — drop the cached DOB so the full-replace PUT removes it
    // (PTV2-1306). Mirrors the dateOfDeath clear below; without this the stale
    // spread value survived and the cleared DOB reappeared after save.
    delete base.dateOfBirth;
  } else {
    const dob = partialDateToDto(vm.dateOfBirth);
    if (dob !== undefined) base.dateOfBirth = dob;
  }
  if (vm.dateOfDeath === null) {
    // Explicit clear — drop whatever was on the cached record so the PUT
    // reflects the user's intent (e.g. life status flipped back to ALIVE).
    delete base.dateOfDeath;
  } else {
    const dod = partialDateToDto(vm.dateOfDeath);
    if (dod !== undefined) base.dateOfDeath = dod;
  }
  if (vm.sex !== null) {
    base.sex = SEX_TO_DTO[vm.sex];
  } else {
    // Carry over whatever was on the cached record, normalized into the
    // live `SexOptions` enum (the cache may hold `'male'`/`'female'`
    // from the OpenAPI long-form contract).
    const carryover = normalizeSexForLiveWrite(existing?.sex);
    if (carryover !== undefined) base.sex = carryover;
    else delete (base as { sex?: LiveSexCode }).sex;
  }
  if (vm.genderIdentity !== null) {
    base.gender = vm.genderIdentity;
  } else if (existing?.gender !== undefined) {
    base.gender = null;
  }
  const ls = mergeLifeStatus(existing?.lifeStatus, vm);
  if (ls !== undefined) base.lifeStatus = ls;
  base.contacts = mergeContacts(existing?.contacts ?? [], vm.contactPoints);
  base.addresses = mergeAddresses(existing?.addresses ?? [], vm.addresses);
  base.primaryCareProvider = vm.primaryCareProvider;
  base.referringProvider = vm.referringProvider;
  base.interpreterRequired = vm.interpreterRequired;
  base.interpreterLanguage = vm.interpreterLanguage;
  base.ethnicities = mergeEthnicities(existing?.ethnicities ?? [], vm.ethnicities);
  // Explicit chart edits always carry a value (the empty card is `physicalChart: null`,
  // which the BE treats as a clear). Omit the key entirely when neither the
  // form nor the cached record has anything — matches the contract's
  // "omitting the field preserves the stored chart" semantics.
  const chart = mergePhysicalChart(existing?.physical_chart ?? null, vm.physicalChart);
  if (chart !== undefined) base.physical_chart = chart;
  return base;
}

type PersonNameDto = NonNullable<DemographicsDto['names']>[number];
type LifeStatusDto = NonNullable<DemographicsDto['lifeStatus']>;

function mergeNames(
  existing: readonly PersonNameDto[],
  vm: DemographicsViewModel,
): PersonNameDto[] {
  // PTV2-1306: apply first/last unconditionally so an emptied field actually
  // clears. `null` = explicit clear → omit the key (the DTO models these as
  // optional strings and the full-replace save nulls an omitted column); a
  // string overwrites. Previously a null was treated as "leave unchanged", so
  // clears were silently dropped and the old value reappeared after save.
  const head: PersonNameDto = existing[0] !== undefined ? { ...existing[0] } : {};
  if (vm.firstName !== null) head.firstName = vm.firstName;
  else delete head.firstName;
  if (vm.lastName !== null) head.lastName = vm.lastName;
  else delete head.lastName;
  if (vm.lastNameAtBirth !== null) head.lastNameAtBirth = vm.lastNameAtBirth;
  else delete head.lastNameAtBirth;
  if (existing.length === 0) {
    // Don't materialize an empty name record when there's nothing to store.
    return head.firstName === undefined &&
      head.lastName === undefined &&
      head.lastNameAtBirth === undefined
      ? []
      : [head];
  }
  return [head, ...existing.slice(1)];
}

function mergeLifeStatus(
  existing: LifeStatusDto | undefined,
  vm: DemographicsViewModel,
): LifeStatusDto | undefined {
  const next = vm.lifeStatus;
  if (next === null) return existing;
  if (!LIFE_STATUS_TO_DTO.has(next as 'ALIVE')) return existing;
  return {
    ...(existing ?? {}),
    lifeStatusType: next as NonNullable<LifeStatusDto['lifeStatusType']>,
    // Alive & Well is a pedigree adjunct (only meaningful when ALIVE). Carry it through the
    // full-replace save so the graph's A&W toggle and the panel stay in sync (PTV2-1318).
    aliveAndWell: vm.aliveAndWell,
    // Applied unconditionally so an emptied field actually clears, same as the name parts.
    causeOfDeath: vm.causeOfDeath,
    // Pregnancy dating is one anchor plus the basis it was asserted on (PTV2-1586) — written as a
    // pair so a cleared anchor can never be read under a stale basis.
    pregnancyDatingAnchor: vm.pregnancyDatingAnchor,
    pregnancyDatingBasis: vm.pregnancyDatingBasis,
    pregnancyDatingAsOf: vm.pregnancyDatingAsOf,
  };
}

/** Match by (type, value) so existing ids stay stable when the user reorders or edits in place. */
function mergeContacts(
  existing: readonly ContactPointDto[],
  next: readonly ContactPointViewModel[],
): ContactPointDto[] {
  const used = new Set<number>();
  return next.map((vm) => {
    let priorIdx = existing.findIndex(
      (c, i) => !used.has(i) && c.type === vm.type && c.value === vm.value,
    );
    if (priorIdx === -1) {
      // No exact match; fall back to a same-type slot so we still preserve
      // the id when the user only changed the value.
      priorIdx = existing.findIndex((c, i) => !used.has(i) && c.type === vm.type);
    }
    const prior = priorIdx === -1 ? undefined : existing[priorIdx];
    if (priorIdx !== -1) used.add(priorIdx);
    // `use` is a NOT NULL column on the BE; existing rows carry it forward via
    // the spread, but a freshly-added contact (no prior match) needs a default.
    // Patient-creation also defaults to HOME — keep that consistent so the wire
    // shape doesn't drift between POST /persons and PUT /demographics.
    const out: ContactPointDto =
      prior !== undefined
        ? { ...prior, type: vm.type, value: vm.value, preferred: vm.preferred }
        : { use: 'HOME', type: vm.type, value: vm.value, preferred: vm.preferred };
    if (vm.use !== null) {
      out.use = vm.use;
    }
    if (out.use === undefined) {
      // Defensive: if `prior` exists but its `use` was somehow missing
      // (legacy row pre-`use` column?), still send a sane default rather
      // than letting the DB NOT NULL constraint trip the whole save.
      out.use = 'HOME';
    }
    stripBlankGuid(out);
    return out;
  });
}

/**
 * Match by `type` so the BE row id stays stable when the user edits terms in
 * place. Wire shape requires `EthnicityDTO.type` (`@NotNull`); rows with empty
 * `terms` are dropped so we don't send junk PATIENT/MATERNAL/PATERNAL slots.
 */
function mergeEthnicities(
  existing: readonly EthnicityDto[],
  next: readonly EthnicityViewModel[],
): EthnicityDto[] {
  const used = new Set<number>();
  const out: EthnicityDto[] = [];
  for (const vm of next) {
    if (vm.terms.length === 0) continue;
    const priorIdx = existing.findIndex((row, i) => !used.has(i) && row.type === vm.type);
    const prior = priorIdx === -1 ? undefined : existing[priorIdx];
    if (priorIdx !== -1) used.add(priorIdx);
    const terms: EthnicityTermDto[] = vm.terms.map((term) => {
      const dto: EthnicityTermDto = { label: term.label };
      if (term.termId !== null) dto.termId = term.termId;
      return dto;
    });
    const row: EthnicityDto = prior !== undefined ? { ...prior, type: vm.type, terms } : { type: vm.type, terms };
    stripBlankGuid(row);
    out.push(row);
  }
  return out;
}

/**
 * Decide what to send for `physical_chart` on a PUT. The contract treats
 * omission as "preserve" and a `null` (or all-blank block) as "clear"; we
 * return `undefined` to skip the field, `null` to clear, or the populated
 * block to write. Always preserve the existing row id so the BE keeps the
 * audit lineage stable.
 */
function mergePhysicalChart(
  existing: PatientPhysicalChartDto | null,
  vm: PhysicalChartViewModel | null,
): PatientPhysicalChartDto | null | undefined {
  if (vm === null) {
    // Nothing in the form. If the cached record also had no chart, skip the
    // field entirely so we don't gratuitously toggle the BE state.
    return existing === null ? undefined : null;
  }
  const hasAny =
    vm.date !== null ||
    (vm.location !== null && vm.location.length > 0) ||
    vm.staffMemberUserGuid !== null ||
    (vm.notes !== null && vm.notes.length > 0);
  if (!hasAny) return existing === null ? undefined : null;
  const out: PatientPhysicalChartDto = {
    ...(existing ?? {}),
    date: vm.date,
    location: vm.location,
    staff_member_user_guid: vm.staffMemberUserGuid,
    notes: vm.notes,
  };
  stripBlankGuid(out);
  return out;
}

/** Match by (line, city, postalCode) so unsurfaced fields (addressLine2, addressType, etc.) survive. */
function mergeAddresses(
  existing: readonly AddressDto[],
  next: readonly AddressViewModel[],
): AddressDto[] {
  const used = new Set<number>();
  return next.map((vm) => {
    const idx = existing.findIndex(
      (a, i) =>
        !used.has(i) &&
        (a.addressLine1 ?? null) === vm.line &&
        (a.city ?? null) === vm.city &&
        (a.postalCode ?? null) === vm.postalCode,
    );
    const prior = idx === -1 ? undefined : existing[idx];
    if (idx !== -1) used.add(idx);
    const out: AddressDto = prior !== undefined ? { ...prior } : {};
    if (vm.line !== null) out.addressLine1 = vm.line;
    out.addressLine2 = vm.line2;
    if (vm.city !== null) out.city = vm.city;
    out.district = vm.district;
    if (vm.province !== null) out.province = vm.province;
    if (vm.postalCode !== null) out.postalCode = vm.postalCode;
    if (vm.country !== null) out.country = vm.country;
    stripBlankGuid(out);
    return out;
  });
}

// 8-4-4-4-12 hex with hyphens, case-insensitive — same shape Hibernate's
// `@GUID` validator accepts on the BE.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * BE GUID validator (`@GUID` on `ContactPointDTO.id`, `AddressDTO.id`,
 * `DemographicsDTO.id`) rejects anything that isn't a real UUID — null, blank,
 * numeric, or string-but-not-UUID-shaped. The cached `existing` payload from
 * `getPerson` can carry any of those for legacy/unmigrated rows, so we drop
 * the field unless it's a properly-formatted UUID. Mutates in place.
 */
function stripBlankGuid(dto: { id?: string | number | null }): void {
  const id = dto.id;
  if (typeof id === 'string' && UUID_RE.test(id.trim())) {
    return;
  }
  delete dto.id;
}

function identifierToDto(vm: PersonIdentifierViewModel): PersonIdentifierDto {
  // BE requires `label` non-null; default to `userCreatedId` for entries
  // (e.g. newly added rows) the UI hasn't given an explicit label.
  const out: PersonIdentifierDto = {
    userCreatedId: vm.identifierType,
    label: vm.label ?? vm.identifierType,
    value: vm.value,
  };
  if (vm.id !== null) {
    const numericId = Number.parseInt(vm.id, 10);
    if (Number.isFinite(numericId)) out.id = numericId;
  }
  return out;
}

function identifierFromDto(dto: PersonIdentifierDto): PersonIdentifierViewModel {
  return {
    id: typeof dto.id === 'number' ? String(dto.id) : null,
    identifierType: dto.userCreatedId ?? '',
    label: dto.label ?? null,
    value: dto.value ?? '',
  };
}
